import { createSelector } from 'reselect';

// Selectors
import { breadcrumbSelector } from '../../workspace/breadcrumbs/breadcrumbSelector';
import { boardVisibleElementGraphSelector } from '../selectors/elementGraphSelector';
import {
    getIsAsyncResourceEntityFetched,
    getIsAsyncResourceEntityFetching,
} from '../../utils/services/http/asyncResource/asyncResourceSelector';

// Utils
import { getElements } from '../utils/elementStoreUtils';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getLabels, getPhysicalId, getTitle } from '../../../common/elements/utils/elementPropertyUtils';
import { getIsBoardShared } from '../board/boardUtils';
import { getUniqueArray } from '../../../common/utils/arrayUtils';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { Label, LabelMap, LabelsByBoards } from '../../../common/labels/labelTypes';
import { ResourceTypes } from '../../utils/services/http/asyncResource/asyncResourceConstants';

type LabelPath = ImMNElement[];

export const selectLabels = (state: Immutable.Map<string, unknown>) => state.get('labels') as LabelMap | undefined;

export const selectLabelsByIds =
    (labelIds?: string[]) =>
    (state: Immutable.Map<string, object>): Label[] => {
        if (!labelIds || labelIds.length === 0) return [];

        const allLabels = selectLabels(state) || {};
        return labelIds.map((labelId: string) => allLabels[labelId]).filter(Boolean);
    };

export const selectLabelPaths = createSelector(breadcrumbSelector, getElements, (breadcrumbs, elements) => {
    const userRootPath: ImMNElement[] =
        breadcrumbs.path?.map((elementId) => {
            const element = getElement(elements, elementId);
            return getElement(elements, getPhysicalId(element));
        }) || [];

    let hasFoundSharedBoard = false;
    const shareRootPath: ImMNElement[] = [...userRootPath].reverse().reduce((acc: LabelPath, board: ImMNElement) => {
        if (hasFoundSharedBoard) return acc;

        if (getIsBoardShared(board)) hasFoundSharedBoard = true;

        acc.push(board);
        return acc;
    }, []);
    shareRootPath.reverse();

    return { userRootPath, shareRootPath };
});

export const selectBoardsToFetchForCurrentPath = createSelector(
    breadcrumbSelector,
    (state: Immutable.Map<string, unknown>) => state,
    (breadcrumbs, state): { toFetch: string[]; isBoardFetchInProgress: boolean } => {
        const toFetch: string[] = [];
        let isBoardFetchInProgress = false;

        breadcrumbs?.path?.forEach((boardId: string) => {
            if (getIsAsyncResourceEntityFetching(state, ResourceTypes.boards, boardId)) {
                if (!isBoardFetchInProgress) isBoardFetchInProgress = true;
                return;
            }

            if (!getIsAsyncResourceEntityFetched(state, ResourceTypes.boards, boardId)) toFetch.push(boardId);
        });

        return {
            toFetch,
            isBoardFetchInProgress,
        };
    },
);

export const selectCurrentPathLabels = createSelector(
    breadcrumbSelector,
    getElements,
    boardVisibleElementGraphSelector,
    selectLabels,
    (breadcrumbs, elements, visibleElementsGraph, labelMap = {}): LabelsByBoards => {
        const usedLabelIds = new Set<string>();
        return (
            breadcrumbs?.path?.reduce((acc: LabelsByBoards, boardId: string): LabelsByBoards => {
                const visibleElements = visibleElementsGraph?.[boardId] || [];
                const boardLabelIds: string[] = [];

                visibleElements.forEach((elementId) => {
                    const element = getElement(elements, elementId);
                    const labels = getLabels(element);
                    if (!labels || !labels.length) return;

                    boardLabelIds.push(...labels);
                });

                // Make sure we aren't including labels that have already been included from a parent board
                const uniqueBoardLabelIds = getUniqueArray(boardLabelIds).filter(
                    (labelId: string) => !usedLabelIds.has(labelId),
                );

                if (!uniqueBoardLabelIds.length) return acc;

                uniqueBoardLabelIds.forEach((labelId) => usedLabelIds.add(labelId));

                const board = getElement(elements, boardId);
                const boardLabels = uniqueBoardLabelIds.map((labelId) => labelMap[labelId]).filter(Boolean) as Label[];

                acc.push({
                    title: getTitle(board),
                    labels: boardLabels,
                });

                return acc;
            }, []) || []
        );
    },
);
